// =====================================================================
// src/pages/intl-acca/SlipDetailPage.tsx
// =====================================================================
// Single-slip "probability document" surface (PRD §2.3 / §3.1).
// Each leg renders with full reasoning_note (NEVER hidden — PRD §12)
// + pricing audit + CLV cell.
//
// Routes mounted at: /intl-acca/slip/:slipId

import type {
  IntlAccaSlipWithLegs,
  IntlAccaTournament,
} from "../../db/intl-acca-types";
import { TournamentStateBanner } from "../../components/intl-acca/TournamentStateBanner";
import { IntlAccaSlipCard } from "../../components/intl-acca/IntlAccaSlipCard";
import { TierLockedTeaser } from "../../components/intl-acca/TierLockedTeaser";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";

export interface SlipDetailPageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  slip: IntlAccaSlipWithLegs | null;
  matchLabels?: Record<string, string>;
  /** Whether the current viewer's tier covers this slip type. */
  hasAccess: boolean;
  onUpgrade?: () => void;
  onShare?: () => void;
  onPlace?: () => void;
}

export function SlipDetailPage(props: SlipDetailPageProps): JSX.Element {
  const { loading, error, tournament, slip, matchLabels, hasAccess, onUpgrade, onShare, onPlace } = props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading slip…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState variant="custom" title="Could not load slip." body={error} />
      </div>
    );
  }

  if (!slip) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState variant="custom" title="Slip not found." body="The slip id does not exist or you do not have access." />
      </div>
    );
  }

  const gate =
    slip.slip_type === "FOCUS" ? "focus_acca" :
    slip.slip_type === "VALUE" ? "value_acca" :
    "power_acca";

  return (
    <div className="ia-root">
      {tournament ? <TournamentStateBanner tournament={tournament} /> : null}

      <div className="ia-page-pad" style={{ maxWidth: 920, margin: "0 auto" }}>
        {hasAccess ? (
          <IntlAccaSlipCard
            slip={slip}
            matchLabels={matchLabels}
            onShare={onShare}
            onPlace={onPlace}
          />
        ) : (
          <TierLockedTeaser gate={gate} onUpgrade={onUpgrade}>
            <IntlAccaSlipCard slip={slip} matchLabels={matchLabels} />
          </TierLockedTeaser>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: slip } = await supabase.from('intl_acca_slips')
//                              .select('*').eq('id', slipId).single();
// const { data: legs } = await supabase.from('intl_acca_slip_legs')
//                              .select('*').eq('slip_id', slipId)
//                              .order('created_at');
// const { data: tournament } = await supabase.from('intl_acca_tournaments')
//                                    .select('*').eq('id', slip.tournament_id).single();
//
// const hasAccess = tierMeets(currentTier,
//   slip.slip_type === 'FOCUS' || slip.slip_type === 'VALUE' ? 'sika_pro' : 'phantom_pro');
